import { Button, ModalDrawer, RangeGraphV2, RangeSliderV2, RangeSliderVariantV2 } from '@egw/components'
import {
  type InvestmentRiskToleranceCategory,
  type EnrollmentInvestmentRiskToleranceOutput,
} from '@egw/api/src/models/enrollment/enrollment'
import { type Dispatch, type SetStateAction, type ChangeEvent, type FC, useMemo } from 'react'
import { useTranslation } from '@egw/i18n'
import { CUSTOMER_SERVICE_PHONE_NUMBER, CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY } from '@egw/constants'
import { RiskReturnAssetAllocation } from '../risk-return-asset-allocation/risk-return-asset-allocation'
import { ExperienceName } from '../../constants/api/experience-name'

interface ModalRiskToleranceProps {
  isOpen: boolean
  onClose: () => void
  riskToleranceData?: RiskTolerance
  setRiskToleranceData?: Dispatch<SetStateAction<RiskTolerance | undefined>>
  riskTolerances: EnrollmentInvestmentRiskToleranceOutput[]
  handleContinueClick: () => void
  withContainer?: boolean
  isShowCurrentValue?: boolean
  allowedRiskTolerances?: InvestmentRiskToleranceCategory[]
  recommendedValue?: RiskTolerance
  title: string
  initRiskToleranceData?: RiskTolerance | undefined
  experience?: string
}

export interface Allocations {
  cashEquivalentPercent: number
  equityPercent: number
  fixedIncomePercent: number
}

export interface RiskTolerance {
  category: string
  index: number
  allocation: Allocations
  initIndex?: number
}

export const ModalRiskTolerance: FC<ModalRiskToleranceProps> = ({
  isOpen,
  onClose,
  handleContinueClick,
  riskTolerances,
  allowedRiskTolerances,
  recommendedValue,
  riskToleranceData,
  setRiskToleranceData,
  title,
  initRiskToleranceData,
  experience = ExperienceName.BBQ,
}) => {
  const { t } = useTranslation('features-enrollment')

  const isFirstAccount = experience === ExperienceName.BBQ

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newValue = Number(e.target.value)
    const newRiskTolerance = riskTolerances.find((item) => item.index === newValue)
    setRiskToleranceData?.(newRiskTolerance)
  }
  // Slider filled area
  const filledArea = useMemo(() => {
    return riskTolerances
      .reduce<number[]>((acc, item) => {
        if (allowedRiskTolerances?.includes(item.category)) {
          acc.push(item.index)
        }
        return acc
      }, [])
      .sort((a, b) => a - b)
  }, [riskTolerances, allowedRiskTolerances])

  // Returns true if currentRiskTolerance category is not in the recommended range
  const isNotInRecommendedRange = useMemo(() => {
    return !allowedRiskTolerances?.includes(riskToleranceData?.category as InvestmentRiskToleranceCategory)
  }, [allowedRiskTolerances, riskToleranceData])

  const handleCloseModal = (): void => {
    setRiskToleranceData?.(initRiskToleranceData)
    onClose()
  }

  return (
    <ModalDrawer isOpen={isOpen} title="" onClose={handleCloseModal} testId="risk-tolerance-modal">
      <div className="flex flex-col w-full">
        <div className="!pb-0 h-full flex flex-col pb-0">
          <p className="typo-primary-2xl-light text-header mb-md">{t('investments.risk-return.title')} </p>
          <p className="typo-primary-sm-semibold text-text-meta mb-sm mt-sm">
            {t('investments.risk-return.asset_allocation')}
          </p>

          <RiskReturnAssetAllocation currentRiskTolerance={riskToleranceData?.allocation} />
          <p className="typo-primary-sm-semibold text-text-meta -mb-md mt-xl">
            {t('investments.risk-return.expected_return')}
          </p>
          <RangeGraphV2 value={riskToleranceData?.index ?? 0} style={{ width: '99%', height: '260px' }} />
        </div>

        <div className="bg-white bottom-0 left-0 right-0">
          <div className={`!pt-md flex flex-col ${isNotInRecommendedRange ? 'mb-0 !pb-sm' : ''}`}>
            <p className="typo-primary-md-semibold text-text-header mb-2xs">
              {t('investments.risk-return.risk_tolerance')}
            </p>
            <p className="typo-primary-sm-regular text-text-body mb-sm">
              {t('investments.risk-return.risk_tolerance_description')}
            </p>
            <p className={`typo-primary-xl-semibold capitalize mb-xl ${isNotInRecommendedRange ? '' : 'text-sky-400'}`}>
              {title}
            </p>

            <RangeSliderV2
              variant={RangeSliderVariantV2.Split}
              fillFrom={filledArea[0] ?? 0}
              fillTo={filledArea[filledArea.length - 1] ?? 0}
              recommendedLabel="Recommended"
              onChange={onChange}
              label={t('common.current')}
              value={riskToleranceData?.index ?? 0}
              recommendedValue={recommendedValue?.initIndex}
              isShowRecommendedLabel={!isNotInRecommendedRange}
              testId="range-slider"
            />

            {isNotInRecommendedRange ? (
              <div className="p-sm bg-background-light -mt-2xs z-[1]" data-testid="not-in-recommended-range">
                <p className="typo-primary-sm-regular mt-sm">
                  {isFirstAccount
                    ? t('investments.risk-return.outside_range')
                    : t('investments.risk-return.outside_range_teriyaki')}
                </p>
                <p className="typo-primary-sm-regular mt-xs">
                  {t('common.call')}&nbsp;
                  <a
                    href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
                    className="typo-primary-sm-semibold  text-text-action"
                  >
                    {CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY}
                  </a>
                </p>
              </div>
            ) : null}
            <Button
              type="submit"
              className={`w-full md:w-min ${isNotInRecommendedRange ? 'mt-sm' : 'mt-3xl'}`}
              isDisabled={isNotInRecommendedRange}
              testId="enrollment-continue-button"
              onClick={() => {
                handleContinueClick()
              }}
            >
              {t('common.continue')}
            </Button>
          </div>
        </div>
      </div>
    </ModalDrawer>
  )
}
